import { Apartment, ContentCopy, Delete, Edit, InsertDriveFile, MoreVert, Person, Star, Workspaces } from "@mui/icons-material";
import { AlertColor, Box, Divider, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { KeyDriveDialog } from "../../../../../components/KeydriveComponents/Dialog/KeyDriveDialog";
import { useProfile } from "../../../../../context/ProfileProvider/useProfile";
import { CloneWorkspace, Delete as DeleteWorkspace } from '../../../../../services/WorkSpaceServices';
import { AddWorkspaceLocalStorage } from "../../../../PublicLayout/RedirectManager";
import { IFavoriteModel } from "../../../../../model/ItemModel";
import { createFavorite, deleteFavorite } from "../../../../../services/favoriteServices";
import { verifyWorkpace } from "../../../../../services/paywallService";
import { PaywallModal } from "../../../../../components/PaywallModal/PaywallModal";
import { PaywallInformation } from "../../../../../model/PaywallModel";
import useExtractPlan from "../../../../../hooks/useExtractPlan";
import { MaxWorkspaceIcon } from "../../../../../assets/icons/MaxWorkspaceIcon";
import KeydriveSnackbar from "../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar";

interface IWorkspaceCard {
    name: string;
    description: string;
    users: number;
    documents: number;
    index: number;
    workspacesLength: number;
    id: string;
    setLoading: (set: boolean) => void;
    setRefresh: (set: boolean) => void;
    favorites: IFavoriteModel[];
    itemId: string;
}

interface IWorkspaceMenuOptions {
    text: string;
    redirect: string;
    icon: React.ReactNode;
    onClick: (e: any) => void;
}

export const WorkspaceCard: React.FC<IWorkspaceCard> = ({
    name,
    description,
    users,
    documents,
    index,
    workspacesLength,
    id,
    setLoading,
    setRefresh,
    favorites,
    itemId
}) => {

    const { t } = useTranslation();
    const { nameOrg } = useParams();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { preferences, login, id: userId } = useProfile();
    const navigate = useNavigate();
    const [isFav, setIsFav] = useState<boolean>(false);
    const [openKeyDriveDialog, setKeyDriveDialog] = useState<boolean>(false);
    const [paywallInfo, setPaywallInfo] = useState<PaywallInformation>();
    const [paywallOpen, setPaywallOpen] = useState<boolean>(false);
    const { planName } = useExtractPlan();
    const [snackMessage, setSnackMesssage] = useState<string>("");
    const [isOpenSnack, setIsOpenSnack] = useState<boolean>(false);
    const [snackSeverity, setSnackSeverity] = useState<AlertColor>("success");

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
      setAnchorEl(null);
    };
    
    function handleToggleDialog() : void
    {
        if (!openKeyDriveDialog) {
            setKeyDriveDialog(true)
        } else {
            setKeyDriveDialog(false)
        }
    }

    function deleteWorkspace() : void
    {
        setLoading(true);
        DeleteWorkspace(id!)
        .then(() => {
            setKeyDriveDialog(false);
        })
        .catch(() => {})
        .finally(() => {
            setLoading(false);
            setRefresh(true);
        })
    }

    function cloneWorkspace() : void 
    {
        verifyWorkpace(nameOrg!)
        .then(data => {
            if (!data?.canDo) {
                setPaywallInfo(data);
                setPaywallOpen(true);
            } else {
                CloneWorkspace({ name: name!, id: id! })
                .then(data => {

                })
                .catch()
                .finally(() => {
                    setRefresh(true);
                })
            }
        })
        .catch((ex) => {
            setSnackMesssage(ex.response.data);
            setIsOpenSnack(true);
            setSnackSeverity("error");
        })
    }

    async function favotiteWorkspaceHandler(favorite: IFavoriteModel, nameOrg: string)
    {
        if (!favorites.flatMap(x => x.favoriteName).includes(name!)) 
        {
            try {
                setLoading(true);
                await createFavorite({
                    extension: "",
                    favoriteName: name!,
                    favoriteType: 1,
                    organizationInternalName: nameOrg!,
                    itemId: id
                });
            } catch {

            } finally {
                setLoading(false);
                setRefresh(true);
            }
        } else 
        {
            try {
                setLoading(true);
                await deleteFavorite(nameOrg!, userId!, itemId!);
            } catch {

            } finally {
                setLoading(false);
                setRefresh(true);
            }
        }
    }

    const options: IWorkspaceMenuOptions[] = [
        {
            redirect: "",
            text: t("AdminConsole.Duplicate"),
            icon: <ContentCopy />,
            onClick: (e) => {
                e.stopPropagation();
                cloneWorkspace()
            }
        },
        {
            redirect: "",
            text: t("AdminConsole.Edit"),
            icon: <Edit /> ,
            onClick: (e) => {
                e.stopPropagation();
                navigate(`/${nameOrg}/adminConsole/workspaces/config/${id!}`)
            }
        },
        {
            redirect: "",
            text: t("AdminConsole.Delete"),
            icon: <Delete />,
            onClick: (e) => {
                e.stopPropagation();
                setKeyDriveDialog(true)
            }
        },
        {
            redirect: "",
            text: !favorites.flatMap(fav => fav.favoriteName).includes(name) ? t("AdminConsole.Favorite") : t("AdminConsole.Unfavorite"),
            icon: !favorites.flatMap(fav => fav.favoriteName).includes(name) ? <Star sx={{ opacity: 0.5 }} /> : <Star />,
            onClick: (e) => {
                e.stopPropagation();
                favotiteWorkspaceHandler({
                    extension: "",
                    favoriteName: name!,
                    id: id!,
                    type: 1,
                    organizationInternalName: nameOrg!,
                    userEmail: login!,
                    userId: userId!,
                    itemId
                }, nameOrg!)
            },
        },
        {
            redirect: "",
            text: "Acessar workspace",
            icon: <Apartment />,
            onClick: (e) => { navigate(`/${nameOrg!}/wsallowed/${id}`) },
        },
    ]

    useEffect(() => {
        if (favorites?.map(fav => fav.favoriteName).includes(name)) {
            setIsFav(true);
        } else {
            setIsFav(false);
        }
    }, [favorites])

    return (
        <Box
            sx={{
                background: "#FFFFFF 0% 0% no-repeat padding-box",
                border: !isFav ? "1px solid #0000001A" : "1px solid #5892cde2",
                borderRadius: "4px",
                opacity: 1,
            }}
            display="flex"
            flexDirection="column"
            border={!isFav ? "1px solid #0000001A" : "1px solid #5892cde2"}
            borderRadius="4px"
            width="236px"
            height="169px"
            onClick={(e) => {
                e.stopPropagation();
                if (anchorEl == null && !openKeyDriveDialog && !paywallOpen) {
                    navigate(`/${nameOrg}/adminConsole/workspaces/${id!}`);
                    AddWorkspaceLocalStorage(id!);

                }
            }}
        >
            <Box
                padding="15px 15px 10px"
                display="flex"
                flexDirection="column"
                gap="3px"
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box
                        width="22px"
                        height="22px"
                        sx={{
                            backgroundColor: "#22638D40"
                        }}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        borderRadius="2px"
                    >
                        <Workspaces 
                            fontSize="small"
                            sx={{
                                color: "#22638D",
                            }}
                        />
                    </Box>
                    <Box
                        width="22px"
                        height="22px"
                    >
                    <IconButton
                        style={{padding: '0'}}
                        aria-label="more"
                        id="long-button"
                        aria-haspopup="true"
                        onClick={handleClick}
                    >
                        <MoreVert />
                    </IconButton>
                    </Box>
                </Box>
                <Typography sx={{
                    font: 'normal normal 600 14px/21px Segoe UI',
                    letterSpacing: '0px',
                    color: '#2D2D2D',
                }}>{ name }</Typography>
                <Typography sx={{
                    font: 'normal normal normal 12px/18px Segoe UI',
                    letterSpacing: '0px',
                    color: '#2D2D2D',
                    opacity: 0.6
                }}>{ description }</Typography>
            </Box>
            <Divider />
            <Box
                padding="10px 15px"
            >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom="14px"
                    >
                        <Box
                            display="flex"
                        >
                            <Box
                                width="22px"
                                height="22px"
                                sx={{
                                    backgroundColor: "#22638D40"
                                }}
                                display="flex"
                                borderRadius="2px"
                                marginRight="5px"
                            >
                                <Person 
                                    fontSize="small"
                                    sx={{
                                        color: "#22638D",
                                    }}
                                />
                            </Box>
                                <Typography sx={{
                                    font: 'normal normal 600 12px/18px Segoe UI',
                                    letterSpacing: '0px',
                                    color: '#2D2D2D',
                                }}>{t("AdminConsole.UsersWithAccess") }</Typography>
                            <Box>
                        </Box>
                    </Box>
                    <Typography sx={{
                        font: 'normal normal 600 12px/18px Segoe UI',
                        letterSpacing: '0px',
                        color: '#2D2D2D',
                    }}>{ users }</Typography>
                </Box>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box
                            display="flex"
                        >
                            <Box
                                width="22px"
                                height="22px"
                                sx={{
                                    backgroundColor: "#22638D40"
                                }}
                                display="flex"
                                borderRadius="2px"
                                marginRight="5px"
                            >
                                <InsertDriveFile 
                                    fontSize="small"
                                    sx={{
                                        color: "#22638D",
                                    }}
                                />
                            </Box>
                                <Typography sx={{
                                    font: 'normal normal 600 12px/18px Segoe UI',
                                    letterSpacing: '0px',
                                    color: '#2D2D2D',
                                }}>{t("AdminConsole.Documents") }</Typography>
                            <Box>
                        </Box>
                    </Box>
                    <Typography sx={{
                        font: 'normal normal 600 12px/18px Segoe UI',
                        letterSpacing: '0px',
                        color: '#2D2D2D',
                    }}>{ documents }</Typography>
                </Box>
            </Box>
            <Menu
                id="long-menu"
                MenuListProps={{
                'aria-labelledby': 'long-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: "159px"
                    }
                }}
                slotProps={{
                    root: {
                        style: {
                            padding: 0
                        }
                    }
                }}
            >
                {options.map((option, index, list) => (
                <MenuItem 
                    key={index}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClose();
                        option.onClick(e);
                    }}
                    sx={{
                        paddingLeft: 0,
                        paddingTop: "3px",
                        paddingBottom: "3px",
                        borderBottom: `${index < list.length - 1 ? "1px solid #0000001A" : ""}`
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box
                            sx={{
                                marginLeft: "4px"
                            }}
                        >
                            { option.icon }
                        </Box>
                        <Typography sx={{
                                font: 'normal normal 600 12px/18px Segoe UI',
                                letterSpacing: '0px',
                                color: '#2D2D2D',
                                marginLeft: "5px"
                        }}>{ option.text }</Typography>
                    </Box>
                </MenuItem>
                ))}
            </Menu>
            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    rightButtonText: t("AdminConsole.Confirm"),
                    leftButtonText: t("AdminConsole.Cancel"),
                    leftButtonMethod: () => { setKeyDriveDialog(false) },
                    rightButtonMethod: () => {
                        deleteWorkspace()
                    }
                }}
                keyDriveDialogContent={{
                    dialogContent: t("AdminConsole.DeleteDialogTextPart1") + `"${name!}" `+ t("AdminConsole.DeleteDialogTextPart2"),
                    dialogIcon: <Delete sx={{
                        width: "40px",
                        height: "44px",
                        color: "#22638D"
                    }} />,
                    dialogTitle: t("AdminConsole.DeleteDialogTitle"),
                    handleTogglDialog: handleToggleDialog,
                    openDialog: openKeyDriveDialog
                }}
                dialogTitleCenter
            />
            <PaywallModal
                icon={<MaxWorkspaceIcon />}
                open={paywallOpen}
                setOpen={setPaywallOpen}
                subtitleStart={paywallInfo?.paywallContent!}
                subtitleBold={` ${planName}`}
                subtitleEnd={paywallInfo?.paywallTitle!}
                paywallContent={paywallInfo?.paywallContent!}
            />
            <KeydriveSnackbar 
                message={snackMessage}
                open={isOpenSnack}
                variant={snackSeverity}
                onClose={handleClose}
            />
        </Box>
    )
}